import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import { supabase } from '../../lib/supabaseClient';

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

const slideUp = keyframes`
    from { opacity: 0; transform: translateY(30px); }
    to { opacity: 1; transform: translateY(0); }
`;

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    animation: ${fadeIn} 0.2s ease;
    padding: 20px;
`;

const Modal = styled.div`
    background: white;
    border-radius: 16px;
    padding: 36px;
    max-width: 460px;
    width: 100%;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    animation: ${slideUp} 0.3s ease;
    position: relative;

    @media (max-width: 500px) {
        padding: 24px;
    }
`;

const CloseBtn = styled.button`
    position: absolute;
    top: 16px;
    right: 16px;
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #999;
    line-height: 1;
    padding: 4px;

    &:hover { color: #333; }
`;

const Title = styled.h2`
    font-size: 22px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0 0 6px 0;
`;

const Subtitle = styled.p`
    font-size: 14px;
    color: #666;
    margin: 0 0 24px 0;
    line-height: 1.5;
`;

const ProjectPreview = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    background: #f8f9fa;
    border-radius: 10px;
    padding: 12px;
    margin-bottom: 24px;
`;

const PreviewImage = styled.div`
    width: 56px;
    height: 56px;
    border-radius: 8px;
    background-size: cover;
    background-position: center;
    background-color: #ddd;
    flex-shrink: 0;
`;

const PreviewInfo = styled.div`
    flex: 1;
    min-width: 0;
`;

const PreviewName = styled.div`
    font-weight: 600;
    font-size: 14px;
    color: #333;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const PreviewDesigner = styled.div`
    font-size: 12px;
    color: #888;
    margin-top: 2px;
`;

const Label = styled.label`
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #444;
    margin-bottom: 6px;
`;

const Input = styled.input`
    width: 100%;
    padding: 12px 14px;
    border: 1.5px solid #ddd;
    border-radius: 10px;
    font-size: 15px;
    outline: none;
    transition: border-color 0.2s;
    box-sizing: border-box;

    &:focus {
        border-color: rgb(24, 51, 189);
        box-shadow: 0 0 0 3px rgba(24, 51, 189, 0.1);
    }

    &::placeholder {
        color: #bbb;
    }
`;

const FormGroup = styled.div`
    margin-bottom: 16px;
`;

const CheckboxRow = styled.label`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    cursor: pointer;
    margin-bottom: 24px;
    font-size: 13px;
    color: #555;
    line-height: 1.4;

    input[type="checkbox"] {
        margin-top: 2px;
        width: 18px;
        height: 18px;
        accent-color: rgb(24, 51, 189);
        flex-shrink: 0;
    }
`;

const SubmitBtn = styled.button`
    width: 100%;
    padding: 14px;
    background: linear-gradient(135deg, rgb(24, 51, 189), rgb(18, 38, 140));
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 4px 16px rgba(24, 51, 189, 0.35);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const ErrorMsg = styled.div`
    background: #fff0f0;
    color: #c0392b;
    padding: 10px 14px;
    border-radius: 8px;
    font-size: 13px;
    margin-bottom: 16px;
    border: 1px solid #f5c6cb;
`;

const checkAnim = keyframes`
    0% { transform: scale(0); }
    50% { transform: scale(1.2); }
    100% { transform: scale(1); }
`;

const SuccessContainer = styled.div`
    text-align: center;
    padding: 12px 0;
`;

const SuccessIcon = styled.div`
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: linear-gradient(135deg, #28a745, #20c997);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    font-size: 32px;
    color: white;
    animation: ${checkAnim} 0.4s ease;
`;

const SuccessTitle = styled.h3`
    font-size: 20px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0 0 8px 0;
`;

const SuccessText = styled.p`
    font-size: 14px;
    color: #666;
    margin: 0 0 24px 0;
    line-height: 1.5;
`;

const ShareRow = styled.div`
    display: flex;
    gap: 10px;
    justify-content: center;
    flex-wrap: wrap;
    margin-bottom: 20px;
`;

const ShareBtn = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 10px 16px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    color: white;
    transition: transform 0.2s;
    cursor: pointer;

    &:hover {
        transform: translateY(-2px);
        color: white;
    }
`;

const CopyLinkBtn = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 10px 16px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    border: 1.5px solid #ddd;
    background: white;
    color: #333;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        border-color: #999;
        background: #f8f8f8;
    }
`;

const DoneBtn = styled.button`
    width: 100%;
    padding: 12px;
    background: #f0f0f0;
    color: #333;
    border: none;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;

    &:hover { background: #e0e0e0; }
`;

const VoteModal = ({ projectName, projectImage, designerName, projectId, onClose, onVoteSuccess, utmParams }) => {
    const { i18n } = useTranslation('common');
    const isFr = i18n.language === 'fr';

    const [email, setEmail] = useState(() => localStorage.getItem('fd100_vote_email') || '');
    const [name, setName] = useState(() => localStorage.getItem('fd100_vote_name') || '');
    const [newsletter, setNewsletter] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [copied, setCopied] = useState(false);

    const savedEmail = localStorage.getItem('fd100_vote_email');
    const hasStoredEmail = savedEmail && savedEmail === email;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        const trimmedEmail = email.trim().toLowerCase();
        if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
            setError(isFr ? 'Veuillez entrer une adresse email valide.' : 'Please enter a valid email address.');
            return;
        }

        setSubmitting(true);

        try {
            const { error: insertError } = await supabase
                .from('fd100_votes')
                .insert({
                    project_id: projectId,
                    voter_email: trimmedEmail,
                    voter_name: name.trim() || null,
                    newsletter_opt_in: newsletter,
                    ...(utmParams && {
                        utm_source: utmParams.utm_source,
                        utm_medium: utmParams.utm_medium,
                        utm_campaign: utmParams.utm_campaign,
                        utm_content: utmParams.utm_content,
                    }),
                });

            if (insertError) {
                if (insertError.code === '23505') {
                    setError(isFr
                        ? 'Vous avez déjà voté pour ce projet avec cette adresse email.'
                        : 'You have already voted for this project with this email address.');
                    setSubmitting(false);
                    return;
                }
                throw insertError;
            }

            localStorage.setItem('fd100_vote_email', trimmedEmail);
            if (name.trim()) localStorage.setItem('fd100_vote_name', name.trim());

            const votedProjects = JSON.parse(localStorage.getItem('fd100_voted_projects') || '[]');
            if (!votedProjects.includes(projectId)) {
                votedProjects.push(projectId);
                localStorage.setItem('fd100_voted_projects', JSON.stringify(votedProjects));
            }

            setSuccess(true);
            if (onVoteSuccess) onVoteSuccess(projectId);
        } catch (err) {
            console.error('Vote error:', err);
            setError(isFr
                ? 'Une erreur est survenue. Veuillez réessayer.'
                : 'An error occurred. Please try again.');
        }

        setSubmitting(false);
    };

    const projectUrl = `${window.location.origin}/vote/fd100/${projectId}`;

    const handleCopyLink = () => {
        navigator.clipboard.writeText(projectUrl).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const shareText = isFr
        ? `Je viens de voter pour "${projectName}" au Prix du Public Le FD100 ! Votez aussi :`
        : `I just voted for "${projectName}" for the Le FD100 People's Choice Award! Vote too:`;

    if (success) {
        return (
            <Overlay onClick={onClose}>
                <Modal onClick={e => e.stopPropagation()}>
                    <CloseBtn onClick={onClose}>&times;</CloseBtn>
                    <SuccessContainer>
                        <SuccessIcon>&#10003;</SuccessIcon>
                        <SuccessTitle>
                            {isFr ? 'Vote enregistré !' : 'Vote recorded!'}
                        </SuccessTitle>
                        <SuccessText>
                            {isFr
                                ? `Merci pour votre vote pour "${projectName}". Partagez pour soutenir ce projet !`
                                : `Thank you for voting for "${projectName}". Share to support this project!`}
                        </SuccessText>
                        <ShareRow>
                            <ShareBtn
                                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(projectUrl)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ background: '#0077B5' }}
                            >
                                LinkedIn
                            </ShareBtn>
                            <ShareBtn
                                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(projectUrl)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ background: '#1877F2' }}
                            >
                                Facebook
                            </ShareBtn>
                            <ShareBtn
                                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(projectUrl)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ background: '#1DA1F2' }}
                            >
                                X
                            </ShareBtn>
                            <CopyLinkBtn onClick={handleCopyLink}>
                                {copied
                                    ? (isFr ? 'Copié !' : 'Copied!')
                                    : (isFr ? 'Copier le lien' : 'Copy link')}
                            </CopyLinkBtn>
                        </ShareRow>
                        <DoneBtn onClick={onClose}>
                            {isFr ? 'Continuer à voter' : 'Continue voting'}
                        </DoneBtn>
                    </SuccessContainer>
                </Modal>
            </Overlay>
        );
    }

    return (
        <Overlay onClick={onClose}>
            <Modal onClick={e => e.stopPropagation()}>
                <CloseBtn onClick={onClose}>&times;</CloseBtn>
                <Title>{isFr ? 'Voter pour ce projet' : 'Vote for this project'}</Title>
                <Subtitle>
                    {isFr
                        ? 'Entrez votre email pour voter. Vous pouvez voter pour autant de projets que vous le souhaitez.'
                        : 'Enter your email to vote. You can vote for as many projects as you like.'}
                </Subtitle>

                <ProjectPreview>
                    <PreviewImage style={{ backgroundImage: projectImage ? `url(${projectImage})` : 'none' }} />
                    <PreviewInfo>
                        <PreviewName>{projectName}</PreviewName>
                        {designerName && <PreviewDesigner>{designerName}</PreviewDesigner>}
                    </PreviewInfo>
                </ProjectPreview>

                <form onSubmit={handleSubmit}>
                    {error && <ErrorMsg>{error}</ErrorMsg>}

                    <FormGroup>
                        <Label>{isFr ? 'Email *' : 'Email *'}</Label>
                        <Input
                            type="email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            placeholder={isFr ? 'votre@email.com' : 'your@email.com'}
                            required
                            autoFocus={!hasStoredEmail}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label>{isFr ? 'Nom (optionnel)' : 'Name (optional)'}</Label>
                        <Input
                            type="text"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            placeholder={isFr ? 'Votre nom' : 'Your name'}
                        />
                    </FormGroup>

                    <CheckboxRow>
                        <input
                            type="checkbox"
                            checked={newsletter}
                            onChange={e => setNewsletter(e.target.checked)}
                        />
                        <span>
                            {isFr
                                ? 'Je souhaite recevoir les actualités du Le FRENCH DESIGN'
                                : 'I would like to receive Le FRENCH DESIGN news'}
                        </span>
                    </CheckboxRow>

                    <SubmitBtn type="submit" disabled={submitting}>
                        {submitting
                            ? (isFr ? 'Envoi...' : 'Submitting...')
                            : (isFr ? 'Voter' : 'Vote')}
                    </SubmitBtn>
                </form>
            </Modal>
        </Overlay>
    );
};

export default VoteModal;
